import React, { useCallback, useState } from 'react';
import { Droplet, Save, RefreshCw, Download, Calculator, AreaChart } from 'lucide-react';
import Button from '../../../../../components/ui/Button';

// Импорт секций формы
import PoolParametersSection from './PoolParametersSection';
import AdditionalParametersSection from './AdditionalParametersSection';
import PoolFeaturesSection from './PoolFeaturesSection';
import MaintenanceSection from './MaintenanceSection';

// Импорт утилитарных функций
import { isPositiveNumber, isInteger } from '../../../../../utils/validation';
import { formatNumber } from '../../../../../utils/formatting';
import { convertUnits } from '../../../../../utils/calculations/converter';

/**
 * Компонент основной формы расчета объема бассейна
 */
const CalculationForm = ({ 
  formData, 
  setFormData, 
  errors, 
  setErrors, 
  isCalculating, 
  setIsCalculating,
  results,
  setResults,
  poolTypes,
  bottomSlopeTypes,
  specificFeatures,
  waterTypes,
  filterSystems,
  saveCalculation,
  initialFormData
}) => {
  
  // Состояние для имени сохраняемого расчета
  const [saveCalculationName, setSaveCalculationName] = useState('');
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  
  // Функция парсинга числа с плавающей точкой или возврат значения по умолчанию
  const parseFloatOrDefault = (val, defaultVal = NaN) => {
    const parsed = parseFloat(String(val).replace(',', '.'));
    return isNaN(parsed) ? defaultVal : parsed;
  };
  
  // Обработчик изменения полей ввода
  const handleInputChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    const fieldValue = type === 'checkbox' ? checked : value;

    setFormData(prev => {
      const newState = { ...prev, [name]: fieldValue };

      // Автоматический пересчет значений при изменении единиц измерения
      if (name === 'unitSystem') {
        const isMetric = value === 'metric';
        
        if (prev.poolType === 'rectangular' || prev.poolType === 'oval') {
          if (isMetric && prev.unitSystem === 'imperial') {
            // Конвертация из имперских в метрические
            newState.length = formatNumber(parseFloatOrDefault(prev.length) * 0.3048, 2);
            newState.width = formatNumber(parseFloatOrDefault(prev.width) * 0.3048, 2);
            newState.depth = formatNumber(parseFloatOrDefault(prev.depth) * 0.3048, 2);
            newState.averageDepth = formatNumber(parseFloatOrDefault(prev.averageDepth) * 0.3048, 2);
          } else if (!isMetric && prev.unitSystem === 'metric') {
            // Конвертация из метрических в имперские
            newState.length = formatNumber(parseFloatOrDefault(prev.length) * 3.28084, 2);
            newState.width = formatNumber(parseFloatOrDefault(prev.width) * 3.28084, 2);
            newState.depth = formatNumber(parseFloatOrDefault(prev.depth) * 3.28084, 2);
            newState.averageDepth = formatNumber(parseFloatOrDefault(prev.averageDepth) * 3.28084, 2);
          }
        } else if (prev.poolType === 'circular') {
          if (isMetric && prev.unitSystem === 'imperial') {
            // Конвертация из имперских в метрические
            newState.diameter = formatNumber(parseFloatOrDefault(prev.diameter) * 0.3048, 2);
            newState.depth = formatNumber(parseFloatOrDefault(prev.depth) * 0.3048, 2);
          } else if (!isMetric && prev.unitSystem === 'metric') {
            // Конвертация из метрических в имперские
            newState.diameter = formatNumber(parseFloatOrDefault(prev.diameter) * 3.28084, 2);
            newState.depth = formatNumber(parseFloatOrDefault(prev.depth) * 3.28084, 2);
          }
        }
      }

      // При изменении типа бассейна сбрасываем ненужные поля
      if (name === 'poolType') {
        if (value === 'rectangular') {
          newState.diameter = initialFormData.diameter;
          newState.a = initialFormData.a;
          newState.b = initialFormData.b;
        } else if (value === 'circular') {
          newState.length = initialFormData.length;
          newState.width = initialFormData.width;
          newState.a = initialFormData.a;
          newState.b = initialFormData.b;
        } else if (value === 'oval') {
          newState.diameter = initialFormData.diameter;
        }
      }

      // При изменении типа дна (плоское, с уклоном) изменяем используемые поля глубины
      if (name === 'bottomSlope') {
        if (value === 'flat') {
          newState.averageDepth = newState.depth;
        }
      }

      return newState;
    });

    // Очищаем ошибку для изменяемого поля
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  }, [errors, setErrors, setFormData]);

  // Обработчик для множественного выбора специфичных особенностей бассейна
  const handleFeaturesChange = useCallback((e) => {
    const { value, checked } = e.target;
    
    setFormData(prev => {
      let newFeatures = [...prev.specificFeatures];
      
      if (checked) {
        newFeatures.push(value);
      } else {
        newFeatures = newFeatures.filter(feature => feature !== value);
      }
      
      return {
        ...prev,
        specificFeatures: newFeatures
      };
    });
  }, [setFormData]);

  // Валидация формы
  const validateForm = useCallback(() => {
    const newErrors = {};
    const { 
      poolType, unitSystem, length, width, depth, diameter, averageDepth,
      waterLevel, bottomSlope, wallThickness, a, b
    } = formData;

    // Проверка позитивных значений на основе типа бассейна
    if (poolType === 'rectangular') {
      const numLength = parseFloatOrDefault(length, -1);
      const numWidth = parseFloatOrDefault(width, -1);
      const numDepth = parseFloatOrDefault(depth, -1);
      
      if (numLength <= 0) newErrors.length = 'Длина должна быть положительным числом';
      if (numWidth <= 0) newErrors.width = 'Ширина должна быть положительным числом';
      if (numDepth <= 0) newErrors.depth = 'Глубина должна быть положительным числом';
      
      const maxSize = unitSystem === 'metric' ? 100 : 330; // 100 метров или 330 футов
      if (numLength > maxSize) newErrors.length = `Максимальная длина ${maxSize} ${unitSystem === 'metric' ? 'м' : 'фт'}`;
      if (numWidth > maxSize) newErrors.width = `Максимальная ширина ${maxSize} ${unitSystem === 'metric' ? 'м' : 'фт'}`;
      
      const maxDepth = unitSystem === 'metric' ? 10 : 33; // 10 метров или 33 фута
      if (numDepth > maxDepth) newErrors.depth = `Максимальная глубина ${maxDepth} ${unitSystem === 'metric' ? 'м' : 'фт'}`;
    } 
    else if (poolType === 'circular') {
      const numDiameter = parseFloatOrDefault(diameter, -1);
      const numDepth = parseFloatOrDefault(depth, -1);
      
      if (numDiameter <= 0) newErrors.diameter = 'Диаметр должен быть положительным числом';
      if (numDepth <= 0) newErrors.depth = 'Глубина должна быть положительным числом';
      
      const maxDiameter = unitSystem === 'metric' ? 50 : 165; // 50 метров или 165 футов
      if (numDiameter > maxDiameter) newErrors.diameter = `Максимальный диаметр ${maxDiameter} ${unitSystem === 'metric' ? 'м' : 'фт'}`;
      
      const maxDepth = unitSystem === 'metric' ? 10 : 33; // 10 метров или 33 фута
      if (numDepth > maxDepth) newErrors.depth = `Максимальная глубина ${maxDepth} ${unitSystem === 'metric' ? 'м' : 'фт'}`;
    }
    else if (poolType === 'oval') {
      const numLength = parseFloatOrDefault(length, -1);
      const numWidth = parseFloatOrDefault(width, -1);
      const numDepth = parseFloatOrDefault(depth, -1);
      
      if (numLength <= 0) newErrors.length = 'Длина должна быть положительным числом';
      if (numWidth <= 0) newErrors.width = 'Ширина должна быть положительным числом';
      if (numDepth <= 0) newErrors.depth = 'Глубина должна быть положительным числом';
      
      if (numWidth >= numLength) newErrors.width = 'Ширина должна быть меньше длины для овального бассейна';
    }
    else if (poolType === 'irregular') {
      const numA = parseFloatOrDefault(a, -1);
      const numB = parseFloatOrDefault(b, -1);
      const numDepth = parseFloatOrDefault(depth, -1);
      
      if (numA <= 0) newErrors.a = 'Значение должно быть положительным числом';
      if (numB <= 0) newErrors.b = 'Значение должно быть положительным числом';
      if (numDepth <= 0) newErrors.depth = 'Глубина должна быть положительным числом';
    }

    // Проверки для всех типов бассейнов
    if (bottomSlope !== 'flat') {
      const numAverageDepth = parseFloatOrDefault(averageDepth, -1);
      const numDepth = parseFloatOrDefault(depth, -1);
      
      if (numAverageDepth <= 0) newErrors.averageDepth = 'Средняя глубина должна быть положительным числом';
      if (numAverageDepth > numDepth) newErrors.averageDepth = 'Средняя глубина не может быть больше максимальной';
      if (numAverageDepth < numDepth * 0.3) newErrors.averageDepth = 'Средняя глубина слишком мала для указанной максимальной';
    }
    
    // Проверка процента заполнения
    const numWaterLevel = parseFloatOrDefault(waterLevel, -1);
    if (numWaterLevel <= 0 || numWaterLevel > 100) {
      newErrors.waterLevel = 'Уровень воды должен быть в пределах от 1% до 100%';
    }
    
    // Проверка толщины стенок
    const numWallThickness = parseFloatOrDefault(wallThickness, -1);
    const maxWallThickness = unitSystem === 'metric' ? 1 : 3.3; // 1 метр или 3.3 фута
    if (numWallThickness < 0) {
      newErrors.wallThickness = 'Толщина стенок не может быть отрицательной';
    } else if (numWallThickness > maxWallThickness) {
      newErrors.wallThickness = `Максимальная толщина стенок ${maxWallThickness} ${unitSystem === 'metric' ? 'м' : 'фт'}`;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, setErrors]);

  // Функция расчета объема бассейна
  const calculateVolume = useCallback(() => {
    if (!validateForm()) {
      // Опционально прокрутка к первой ошибке
      const firstErrorKey = Object.keys(errors)[0];
      if (firstErrorKey) {
        const errorElement = document.getElementById(firstErrorKey);
        errorElement?.focus();
        errorElement?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    setIsCalculating(true);
    setResults(null);

    // Используем setTimeout для обновления UI перед расчетами
    setTimeout(() => {
      try {
        // Парсинг базовых входных данных
        const {
          poolType, unitSystem, length, width, depth, diameter, averageDepth,
          waterLevel, bottomSlope, wallThickness, a, b, specificFeatures,
          stepArea, waterType, maintenanceLevel, filterSystem, poolLocation,
          seasonLength, coverType, heatingSystem, climate
        } = formData;

        // Конвертация в числовые значения
        const numWaterLevel = parseFloatOrDefault(waterLevel, 100) / 100;
        const numWallThickness = parseFloatOrDefault(wallThickness, 0);
        const numStepArea = parseFloatOrDefault(stepArea, 0);
        const numSeasonLength = parseFloatOrDefault(seasonLength, 3);
        
        // Объявляем переменные для расчета
        let surfaceArea = 0;
        let grossVolume = 0;
        let netVolume = 0;
        let liningArea = 0;
        let waterWeight = 0;
        let maintenanceCostAnnual = 0;
        let waterConsumptionAnnual = 0;
        let evaporationLoss = 0;
        let heatingCosts = 0;
        let circulationRate = 0;
        let filterSize = 0;
        let pumpPower = 0;
        
        // Коэффициенты для расчетов
        const waterDensity = 1000; // кг/м³ или 62.4 фунтов/фут³
        const evaporationFactor = poolLocation === 'outdoor' ? 
                                 (climate === 'hot' ? 0.8 : 
                                  climate === 'cold' ? 0.3 : 0.5) : 0.2;
        const heatingFactor = heatingSystem === 'none' ? 0 :
                              heatingSystem === 'electric' ? 1.2 :
                              heatingSystem === 'heat_pump' ? 0.5 :
                              heatingSystem === 'solar' ? 0.1 : 0;
        const coverFactor = coverType === 'none' ? 1 :
                            coverType === 'standard' ? 0.7 :
                            coverType === 'solar' ? 0.4 : 1;
                            
        // Находим соответствующий тип очистки воды
        const waterTypeObj = waterTypes.find(type => type.id === waterType) || { maintenanceCost: 1.0 };
        
        // Находим соответствующую систему фильтрации
        const filterSystemObj = filterSystems.find(system => system.id === filterSystem) || 
                              { maintenanceFactor: 1.0, replacementYears: 5 };
                              
        // Коэффициент обслуживания на основе выбранного уровня
        const maintenanceFactor = maintenanceLevel === 'basic' ? 0.7 :
                                 maintenanceLevel === 'premium' ? 1.5 : 1.0;

        // Расчет в зависимости от типа бассейна
        if (poolType === 'rectangular') {
          const numLength = parseFloatOrDefault(length, 0);
          const numWidth = parseFloatOrDefault(width, 0);
          const numDepth = parseFloatOrDefault(depth, 0);
          const numAverageDepth = bottomSlope === 'flat' ? 
                               numDepth : parseFloatOrDefault(averageDepth, numDepth);
          
          // Поверхностная площадь (зеркало воды)
          surfaceArea = numLength * numWidth;
          
          // Объем воды с учетом типа дна
          if (bottomSlope === 'flat') {
            grossVolume = surfaceArea * numDepth;
          } else {
            grossVolume = surfaceArea * numAverageDepth;
          }
          
          // Площадь облицовки включая стены и дно
          liningArea = 2 * (numLength * numDepth + numWidth * numDepth) + surfaceArea;
        } 
        else if (poolType === 'circular') {
          const numDiameter = parseFloatOrDefault(diameter, 0);
          const radius = numDiameter / 2;
          const numDepth = parseFloatOrDefault(depth, 0);
          
          // Поверхностная площадь (зеркало воды)
          surfaceArea = Math.PI * radius * radius;
          
          // Объем воды
          grossVolume = surfaceArea * numDepth;
          
          // Площадь облицовки включая стены и дно
          liningArea = surfaceArea + (2 * Math.PI * radius * numDepth);
        }
        else if (poolType === 'oval') {
          const numLength = parseFloatOrDefault(length, 0);
          const numWidth = parseFloatOrDefault(width, 0);
          const numDepth = parseFloatOrDefault(depth, 0);
          const radiusLong = numLength / 2;
          const radiusShort = numWidth / 2;
          
          // Поверхностная площадь (зеркало воды) - приближенная формула
          surfaceArea = Math.PI * radiusLong * radiusShort;
          
          // Объем воды
          grossVolume = surfaceArea * numDepth;
          
          // Площадь облицовки (приближенная)
          const perimeter = 2 * Math.PI * Math.sqrt((radiusLong*radiusLong + radiusShort*radiusShort) / 2);
          liningArea = surfaceArea + (perimeter * numDepth);
        }
        else if (poolType === 'irregular') {
          const numA = parseFloatOrDefault(a, 0);
          const numB = parseFloatOrDefault(b, 0);
          const numDepth = parseFloatOrDefault(depth, 0);
          
          // Для нестандартной формы используем приближенную формулу (полуэллипс)
          surfaceArea = (numA * numB * Math.PI) / 2;
          grossVolume = surfaceArea * numDepth;
          
          // Оценка площади облицовки
          const perimeter = Math.PI * (numA + numB); // приближение
          liningArea = surfaceArea + (perimeter * numDepth);
        }
        else if (poolType === 'custom') {
          // Для произвольной формы полагаемся на введенные пользователем значения площади и объема
          // Эта опция требует дополнительных полей ввода
          // В данной реализации используем упрощенный подход через вспомогательные поля a и b
          const numA = parseFloatOrDefault(a, 0); // предполагаем, что это площадь поверхности в данном случае
          const numB = parseFloatOrDefault(b, 0); // предполагаем, что это заданный объем в данном случае
          const numDepth = parseFloatOrDefault(depth, 0);
          
          surfaceArea = numA;
          grossVolume = numB > 0 ? numB : numA * numDepth;
          liningArea = numA + (Math.sqrt(4 * numA) * numDepth); // очень грубая оценка
        }
        
        // Корректировка на толщину стенок (внутренний объем)
        if (numWallThickness > 0) {
          // Упрощенная корректировка для всех типов бассейнов
          const thicknessAdjustment = 1 - (numWallThickness * 2 / parseFloatOrDefault(
            poolType === 'rectangular' || poolType === 'oval' ? width : 
            poolType === 'circular' ? diameter : a, 10));
          
          // Применяем корректировку только если она имеет смысл (не отрицательная)
          if (thicknessAdjustment > 0 && thicknessAdjustment < 1) {
            grossVolume *= Math.pow(thicknessAdjustment, 2); // квадрат для 3D эффекта
            surfaceArea *= thicknessAdjustment;
          }
        }
        
        // Уменьшение объема с учетом лестниц, ступеней и других элементов
        const specificFeaturesVolume = specificFeatures.reduce((total, featureId) => {
          const feature = specificFeatures.find(f => f.id === featureId);
          return total + (feature ? feature.volume : 0);
        }, 0);
        
        // Уменьшение объема за счет ступеней и лестниц (из шага, умноженного на среднюю высоту)
        const stepsVolume = numStepArea * (parseFloatOrDefault(depth, 0) / 2);
        
        // Чистый объем с учетом уровня воды и специфических особенностей
        netVolume = (grossVolume - specificFeaturesVolume - stepsVolume) * numWaterLevel;
        
        // Вес воды
        waterWeight = netVolume * waterDensity;
        
        // Оценка скорости циркуляции воды (полный объем за 6 часов для стандартного обслуживания)
        circulationRate = netVolume / 6; // м³/час
        
        // Подбор размера фильтра (правило 0.4 м²/100 м³)
        filterSize = (netVolume * 0.4) / 100; // м²
        
        // Подбор мощности насоса (примерно 0.25 кВт на 10 м³/час)
        pumpPower = (circulationRate * 0.25) / 10; // кВт
        
        // Расчет годового испарения с учетом климата и расположения
        evaporationLoss = surfaceArea * evaporationFactor * coverFactor * numSeasonLength * 30 * 0.005; // м³ в месяц * месяцы
        
        // Расчет затрат на подогрев
        heatingCosts = netVolume * heatingFactor * (1 - (coverType === 'solar' ? 0.3 : 0)) * numSeasonLength;
        
        // Расчет годового расхода воды (с учетом испарения и частоты замены)
        const waterReplaceFrequency = waterType === 'chlorine' ? 1 : 0.5; // полная/частичная замена в год
        waterConsumptionAnnual = netVolume * waterReplaceFrequency + evaporationLoss;
        
        // Расчет стоимости обслуживания
        // Базовая стоимость в рублях на м³ в год
        const baseMaintenanceCost = 2000; // руб/м³ в год для стандартного обслуживания
        maintenanceCostAnnual = baseMaintenanceCost * netVolume * 
                              waterTypeObj.maintenanceCost * 
                              filterSystemObj.maintenanceFactor * 
                              maintenanceFactor *
                              (numSeasonLength / 12); // пропорционально длительности сезона
        
        // Корректировка для крытых бассейнов
        if (poolLocation === 'indoor') {
          maintenanceCostAnnual *= 1.2; // +20% за счет дополнительной вентиляции и контроля влажности
          heatingCosts *= 0.7; // -30% за счет отсутствия потерь тепла в атмосферу
        }
        
        // Преобразование единиц если необходимо
        if (unitSystem === 'imperial') {
          // Конвертация обратно в метрические для единообразия результатов
          netVolume = convertUnits(netVolume, 'ft3', 'm3', 'volume');
          surfaceArea = convertUnits(surfaceArea, 'ft2', 'm2', 'area');
          liningArea = convertUnits(liningArea, 'ft2', 'm2', 'area');
          waterWeight = netVolume * waterDensity; // пересчитываем вес
          circulationRate = netVolume / 6; // пересчитываем циркуляцию
          filterSize = (netVolume * 0.4) / 100; // пересчитываем фильтр
          pumpPower = (circulationRate * 0.25) / 10; // пересчитываем насос
          evaporationLoss = surfaceArea * evaporationFactor * coverFactor * numSeasonLength * 30 * 0.005;
          waterConsumptionAnnual = netVolume * waterReplaceFrequency + evaporationLoss;
          maintenanceCostAnnual = baseMaintenanceCost * netVolume * 
                              waterTypeObj.maintenanceCost * 
                              filterSystemObj.maintenanceFactor * 
                              maintenanceFactor *
                              (numSeasonLength / 12);
        }
        
        // Вычисление объема в литрах
        const volumeLiters = netVolume * 1000;
        
        // Вычисление площади поверхности в футах (для справки)
        const surfaceAreaFt = convertUnits(surfaceArea, 'm2', 'ft2', 'area');
        
        // Вычисление объема в галлонах (для справки)
        const volumeGallons = convertUnits(netVolume, 'm3', 'gal_us', 'volume');
        
        // Рекомендации по фильтрации
        const filterRecommendation = {
          circulationTime: 6, // часов
          circulationRate: circulationRate, // м³/час
          filterSize: filterSize, // м²
          pumpPower: pumpPower // кВт
        };
        
        // Расчет рекомендаций по химии
        const chemicalRecommendation = {
          chlorineWeekly: (netVolume * 0.03), // кг/неделя (для хлора)
          phRegulatorMonthly: (netVolume * 0.01), // кг/месяц
          algaecideMonthly: (netVolume * 0.005), // л/месяц
          shockChlorineMonthly: (netVolume * 0.05), // кг/месяц
          stabilizer: (netVolume * 0.03) // кг (единоразово)
        };
        
        // Составление результатов расчета
        const calculationResults = {
          // Основные результаты
          grossVolume: grossVolume, // м³
          netVolume: netVolume, // м³
          volumeLiters: volumeLiters, // литры
          volumeGallons: volumeGallons, // галлоны США
          waterWeight: waterWeight, // кг
          
          // Площади
          surfaceArea: surfaceArea, // м²
          surfaceAreaFt: surfaceAreaFt, // ft²
          liningArea: liningArea, // м²
          
          // Эксплуатационные показатели
          evaporationLoss: evaporationLoss, // м³/год
          waterConsumptionAnnual: waterConsumptionAnnual, // м³/год
          maintenanceCostAnnual: maintenanceCostAnnual, // руб/год
          heatingCosts: heatingCosts, // относительные единицы
          
          // Рекомендации
          filterRecommendation: filterRecommendation,
          chemicalRecommendation: chemicalRecommendation,
          
          // Исходные параметры для отображения в результатах
          poolType: poolType,
          unitSystem: unitSystem,
          waterLevel: numWaterLevel * 100
        };
        
        // Устанавливаем результаты
        setResults(calculationResults);
        
      } catch (error) {
        console.error("Ошибка расчета:", error);
        setErrors({ general: `Произошла ошибка при расчете: ${error.message}. Пожалуйста, проверьте введенные данные или попробуйте позже.` });
        setResults(null);
      } finally {
        setIsCalculating(false);
      }
    }, 50);
  }, [formData, validateForm, errors, setErrors, setResults, setIsCalculating, specificFeatures, waterTypes, filterSystems]);

  // Экспорт результатов в CSV
  const exportToCSV = () => {
    if (!results) {
      alert("Нет данных для экспорта. Сначала выполните расчет.");
      return;
    }

    const headers = ['Параметр', 'Значение', 'Единицы измерения'];
    
    const rows = [
      ['ХАРАКТЕРИСТИКИ БАССЕЙНА', '', ''],
      ['Тип бассейна', formatPoolTypeName(formData.poolType), ''],
      ['Объем воды (брутто)', formatNumber(results.grossVolume, 2), 'м³'],
      ['Объем воды (нетто)', formatNumber(results.netVolume, 2), 'м³'],
      ['Объем воды в литрах', formatNumber(results.volumeLiters, 0), 'л'],
      ['Объем воды в галлонах', formatNumber(results.volumeGallons, 0), 'гал США'],
      ['Вес воды', formatNumber(results.waterWeight, 0), 'кг'],
      ['Площадь поверхности', formatNumber(results.surfaceArea, 2), 'м²'],
      ['Площадь облицовки', formatNumber(results.liningArea, 2), 'м²'],
      ['', '', ''],
      ['РАСХОД ВОДЫ И ЭКСПЛУАТАЦИЯ', '', ''],
      ['Ежегодные потери на испарение', formatNumber(results.evaporationLoss, 1), 'м³/год'],
      ['Годовой расход воды', formatNumber(results.waterConsumptionAnnual, 1), 'м³/год'],
      ['Стоимость обслуживания в год', formatNumber(results.maintenanceCostAnnual, 0), 'руб/год'],
      ['', '', ''],
      ['РЕКОМЕНДАЦИИ ПО ФИЛЬТРАЦИИ', '', ''],
      ['Рекомендуемый проток воды', formatNumber(results.filterRecommendation.circulationRate, 1), 'м³/час'],
      ['Площадь фильтра', formatNumber(results.filterRecommendation.filterSize, 2), 'м²'],
      ['Рекомендуемая мощность насоса', formatNumber(results.filterRecommendation.pumpPower, 1), 'кВт'],
      ['', '', ''],
      ['РЕКОМЕНДАЦИИ ПО ХИМИИ', '', ''],
      ['Хлор (еженедельно)', formatNumber(results.chemicalRecommendation.chlorineWeekly, 2), 'кг/неделя'],
      ['Регулятор pH (ежемесячно)', formatNumber(results.chemicalRecommendation.phRegulatorMonthly, 2), 'кг/месяц'],
      ['Альгицид (ежемесячно)', formatNumber(results.chemicalRecommendation.algaecideMonthly, 2), 'л/месяц'],
      ['Ударный хлор (ежемесячно)', formatNumber(results.chemicalRecommendation.shockChlorineMonthly, 2), 'кг/месяц'],
      ['Стабилизатор (единоразово)', formatNumber(results.chemicalRecommendation.stabilizer, 2), 'кг'],
    ];

    // Формируем содержимое CSV
    const csvContent = '\uFEFF' + [headers.join(';'), ...rows.map(row => row.join(';'))].join('\n');
    
    // Создаем и скачиваем файл
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', 'расчет_объема_бассейна.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Форматирование отображаемого имени типа бассейна
  const formatPoolTypeName = (type) => {
    const poolType = poolTypes.find(pt => pt.id === type);
    return poolType ? poolType.name : 'Неизвестный тип';
  };

  // Сброс формы
  const resetForm = () => {
    setFormData({
      poolType: 'rectangular',
      length: '8',
      width: '4',
      depth: '1.5',
      averageDepth: '1.5',
      diameter: '4',
      radius: '2',
      a: '4',
      b: '3',
      waterLevel: '90',
      stepArea: '0',
      unitSystem: 'metric',
      wallThickness: '0.3',
      bottomSlope: 'flat',
      specificFeatures: [],
      maintenanceLevel: 'standard',
      waterType: 'chlorine',
      climate: 'moderate',
      poolLocation: 'outdoor',
      seasonLength: '3',
      coverType: 'none',
      heatingSystem: 'none',
      filterSystem: 'sand',
    });
    setResults(null);
    setErrors({});
    setSaveCalculationName('');
    setShowSaveDialog(false);
  };

  // Обработчик сохранения расчета
  const handleSaveCalculation = () => {
    if (showSaveDialog) {
      const savedResult = saveCalculation(saveCalculationName || null);
      if (savedResult) {
        alert(`Расчет "${savedResult.name}" успешно сохранен!`);
      }
      setShowSaveDialog(false);
      setSaveCalculationName('');
    } else {
      setShowSaveDialog(true);
    }
  };

  // Обработчик отправки формы
  const handleSubmit = (e) => {
    e.preventDefault();
    calculateVolume();
  };

  return (
    <form onSubmit={handleSubmit} noValidate>
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md border border-gray-200 dark:border-gray-700 p-6 sm:p-8 mb-8">
        <h2 className="text-2xl font-semibold mb-6 text-gray-900 dark:text-white flex items-center gap-2">
          <AreaChart className="w-6 h-6 text-blue-500" />
          Расчет объема бассейна
        </h2>

        {/* Общая ошибка */}
        {errors.general && (
          <div role="alert" className="mb-4 p-3 bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-600 rounded-md text-red-700 dark:text-red-300 text-sm">
            {errors.general}
          </div>
        )}

        {/* Основные параметры бассейна */}
        <PoolParametersSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
          poolTypes={poolTypes}
          bottomSlopeTypes={bottomSlopeTypes}
        />

        {/* Дополнительные параметры */}
        <AdditionalParametersSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
        />

        {/* Особенности бассейна */}
        <PoolFeaturesSection 
          formData={formData} 
          handleInputChange={handleInputChange}
          handleFeaturesChange={handleFeaturesChange}
          errors={errors} 
          specificFeatures={specificFeatures}
        />

        {/* Параметры обслуживания */}
        <MaintenanceSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors}
          waterTypes={waterTypes}
          filterSystems={filterSystems}
        />

        {/* Диалог сохранения расчета */}
        {showSaveDialog && (
          <div className="mt-4 mb-6 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800 rounded-lg">
            <div className="flex items-center">
              <input
                type="text"
                placeholder="Введите название для сохранения"
                value={saveCalculationName}
                onChange={(e) => setSaveCalculationName(e.target.value)}
                className="form-input flex-grow mr-3"
                autoFocus
              />
              <Button
                type="button"
                onClick={handleSaveCalculation}
                primary
                size="sm"
              >
                Сохранить
              </Button>
            </div>
          </div>
        )}

        {/* Кнопки действий */}
        <div className="mt-8 flex flex-wrap gap-4">
          <Button
            type="submit"
            primary
            icon={<Calculator className="w-5 h-5" />}
            disabled={isCalculating || Object.keys(errors).length > 0}
            isLoading={isCalculating}
            size="lg"
          >
            {isCalculating ? 'Рассчитываем...' : 'Рассчитать объем'}
          </Button>
          <Button
            type="button"
            onClick={resetForm}
            icon={<RefreshCw className="w-5 h-5" />}
            variant="secondary"
            disabled={isCalculating}
            size="lg"
          >
            Сбросить
          </Button>
          {results && (
            <Button
              type="button"
              onClick={exportToCSV}
              icon={<Download className="w-5 h-5" />}
              variant="secondary"
              disabled={isCalculating}
              size="lg"
            >
              Скачать результаты (CSV)
            </Button>
          )}
          {results && !showSaveDialog && (
            <Button
              type="button"
              onClick={handleSaveCalculation}
              icon={<Save className="w-5 h-5" />}
              variant="secondary"
              disabled={isCalculating}
              size="lg"
            >
              Сохранить расчет
            </Button>
          )}
        </div>
        {Object.keys(errors).length > 0 && !errors.general && (
          <p className="mt-4 text-sm text-red-600 dark:text-red-400" role="alert">Пожалуйста, исправьте ошибки в форме.</p>
        )}
      </div>
    </form>
  );
};

export default CalculationForm;